import {
    zodResidentialAddressLine2Validation,
    zodResidentialAddressValidation,
} from '@fruitful/fruitils';
import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useCallback } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Keyboard, KeyboardAvoidingView, Platform } from 'react-native';
import {
    BodyMediumRegular,
    ControlledInput,
    Form,
    H5Emphasized,
    Private,
    USE_FORM_MODE_DEFAULTS,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { isEmpty } from '../../../../common/misc';
import { US_STATES } from '../../../../common/types/us-states';
import { type User } from '../../../../user/types/User';
import type { RunKYCVerifyBody } from '../../../hooks/kyc/useRunKycVerify';
import KycButton from './KycButton';

const addressSchema = z.object({
    address: zodResidentialAddressValidation,
    apartment: zodResidentialAddressLine2Validation,
    city: z.string().min(1, 'Required field'),
    state: z
        .string()
        .refine(
            (value) => US_STATES.some((state) => state.value === value),
            'Invalid state',
        ),
    zipCode: z.string().length(5, 'Zip code must be exactly 5 digits'),
});

type AddressSchema = z.infer<typeof addressSchema>;

interface AddressProps {
    kycData: Partial<RunKYCVerifyBody>;
    onPressNext: (formData?: Partial<RunKYCVerifyBody>) => void;
    user: User;
}

const Address = ({ kycData, onPressNext, user }: AddressProps) => {
    // A user may already have a legal address on file from filling out their profile
    const {
        register,
        control,
        setFocus,
        handleSubmit,
        watch,
        trigger,
        formState: { errors, isValid },
    } = useForm<AddressSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(addressSchema),
        defaultValues: {
            address: kycData?.line1 ?? user.userAddress?.line1 ?? '',
            apartment: kycData?.line2 ?? user.userAddress?.line2 ?? '',
            city: kycData?.city ?? user.userAddress?.city ?? '',
            state: kycData?.state ?? user.userAddress?.state ?? '',
            zipCode: kycData?.postalCode ?? user.userAddress?.postalCode ?? '',
        },
    });

    const triggerValidationIfAllFieldsFilled = useCallback(async () => {
        const { address, city, state, zipCode } = watch();
        if (address && city && state && zipCode) {
            await trigger();
        }
    }, [watch, trigger]);

    const onSubmit: SubmitHandler<AddressSchema> = ({
        address,
        apartment,
        city,
        state,
        zipCode,
    }) => {
        onPressNext({
            line1: address,
            ...(!isEmpty(apartment) && { line2: apartment }),
            city,
            state,
            postalCode: zipCode,
            countryCode: 'US',
        });
    };

    return (
        <KeyboardAvoidingView
            style={{ flex: 1 }}
            behavior={Platform.OS === 'ios' ? 'position' : 'height'}
            ignoreIOSKeyboardWillChangeEvents
            keyboardVerticalOffset={-90}
            contentContainerStyle={{
                flex: 1,
                display: 'flex',
                justifyContent: 'space-between',
            }}
        >
            <View
                $sm={{
                    flex: 1,
                    justifyContent: 'space-between',
                    gap: 60,
                }}
            >
                <View>
                    <H5Emphasized marginBottom={8}>
                        What&apos;s your home address?
                    </H5Emphasized>
                    <BodyMediumRegular color="$gray1000" marginBottom={24}>
                        Use the residential address where you receive your mail,
                        not a PO Box or commercial address.
                    </BodyMediumRegular>
                    <Private>
                        <Form
                            onSubmit={handleSubmit(onSubmit)}
                            marginBottom={12}
                        >
                            <YStack gap={12}>
                                <ControlledInput
                                    name="address"
                                    placeholder="Street address"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    autoCapitalize="words"
                                    autoComplete="address-line1"
                                    enterKeyHint="next"
                                    onSubmitEditing={() => {
                                        setFocus('apartment');
                                    }}
                                    onBlur={triggerValidationIfAllFieldsFilled}
                                />
                                <ControlledInput
                                    name="apartment"
                                    placeholder="Apt, suite, etc. (optional)"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    autoCapitalize="words"
                                    autoComplete="address-line2"
                                    enterKeyHint="next"
                                    onSubmitEditing={() => {
                                        setFocus('city');
                                    }}
                                    onBlur={triggerValidationIfAllFieldsFilled}
                                />
                                <ControlledInput
                                    name="city"
                                    placeholder="City"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    autoCapitalize="words"
                                    textContentType="addressCity"
                                    enterKeyHint="next"
                                    onSubmitEditing={() => {
                                        setFocus('state');
                                    }}
                                    onBlur={triggerValidationIfAllFieldsFilled}
                                />
                                <XStack gap={12} justifyContent="space-evenly">
                                    <View flex={1}>
                                        <ControlledInput
                                            name="state"
                                            placeholder="State"
                                            control={control}
                                            register={register}
                                            errors={errors}
                                            formatValueOnChange={(
                                                _,
                                                nextValue,
                                            ) => {
                                                // Required for web
                                                return nextValue.toUpperCase();
                                            }}
                                            minWidth="50%"
                                            textContentType="addressState"
                                            autoCapitalize="characters"
                                            enterKeyHint="next"
                                            onSubmitEditing={() => {
                                                setFocus('zipCode');
                                            }}
                                            onBlur={
                                                triggerValidationIfAllFieldsFilled
                                            }
                                            maxLength={2}
                                        />
                                    </View>
                                    <View flex={1}>
                                        <ControlledInput
                                            name="zipCode"
                                            placeholder="ZIP code"
                                            control={control}
                                            register={register}
                                            errors={errors}
                                            minWidth="50%"
                                            autoComplete="postal-code"
                                            type="numeric"
                                            inputMode="numeric"
                                            enterKeyHint="done"
                                            onSubmitEditing={(_) => {
                                                Keyboard.dismiss();
                                            }}
                                            onBlur={
                                                triggerValidationIfAllFieldsFilled
                                            }
                                        />
                                    </View>
                                </XStack>
                            </YStack>
                        </Form>
                    </Private>
                </View>
            </View>
            <KycButton onPress={handleSubmit(onSubmit)} disabled={!isValid} />
        </KeyboardAvoidingView>
    );
};

export default Address;
